/**
 * kevia-conversations — conversation schema utilities.
 * Keeps conversation shaping logic out of useKeviaChat.
 */

/* ─── Time grouping ─────────────────────────────────────────────────── */

/**
 * Groups conversations into temporal buckets for history display.
 * Returns only non-empty buckets, in chronological order (newest first).
 *
 * @param {object[]} conversations — sorted newest-first
 * @returns {{ label: string, items: object[] }[]}
 */
export function groupConversationsByTime(conversations) {
  const now  = Date.now();
  const buckets = {
    hoy:        { label: "Hoy",          items: [] },
    ayer:       { label: "Ayer",         items: [] },
    semana:     { label: "Esta semana",  items: [] },
    anteriores: { label: "Anteriores",   items: [] },
  };

  for (const conv of conversations) {
    const diffH = (now - conv.updatedAt) / 3_600_000;
    if      (diffH <  24) buckets.hoy.items.push(conv);
    else if (diffH <  48) buckets.ayer.items.push(conv);
    else if (diffH < 168) buckets.semana.items.push(conv);
    else                  buckets.anteriores.items.push(conv);
  }

  return Object.values(buckets).filter((g) => g.items.length > 0);
}

/* ─── Smart auto-title ──────────────────────────────────────────────── */

const STRIP_PREFIXES = [
  /^[¿¡]+/,
  /^(puedes?|podr[ií]as?|me\s+puedes?|me\s+podr[ií]as?)\s+/i,
  /^(ay[uú]dame|ay[uú]dame)\s+(a|con)\s+/i,
  /^(necesito|quiero|quisiera)\s+(ver|saber|conocer|entender)?\s*/i,
  /^(dime|expl[ií]came|cu[eé]ntame)\s+/i,
  /^(c[oó]mo|como)\s+(puedo|podr[ií]a|se\s+puede)\s+/i,
  /^(qu[eé]|que)\s+(es|son|significa|debo)\s+/i,
  /^(cu[aá]les?|cu[aá]ntos?)\s+/i,
];

/**
 * Generates a meaningful conversation title from the first user message.
 * Strips common preamble phrases and question indicators.
 *
 * @param {string} text — first user message
 * @returns {string} — clean title ≤52 chars
 */
export function generateConvTitle(text) {
  if (!text?.trim()) return "Nueva conversación";

  let clean = text.trim();

  for (const re of STRIP_PREFIXES) {
    clean = clean.replace(re, "").trim();
  }

  // Strip trailing punctuation
  clean = clean.replace(/[?.!¿¡]+$/, "").trim();

  // Capitalize first letter
  if (clean.length > 1) {
    clean = clean[0].toUpperCase() + clean.slice(1);
  }

  // Truncate with ellipsis
  if (clean.length > 52) clean = clean.slice(0, 49) + "…";

  return clean || "Nueva conversación";
}

/* ─── Conversation factory ──────────────────────────────────────────── */

/**
 * Creates a new conversation object.
 * All optional fields are future-ready (not all used in localStorage v1).
 *
 * @param {{ id: string, text: string, tier: string, entityContext: object|null }} opts
 */
export function makeConversation({ id, text = "", tier = "basic", entityContext = null }) {
  const now = Date.now();
  return {
    id,
    title:           generateConvTitle(text) || "Nueva conversación",
    createdAt:       now,
    updatedAt:       now,
    messages:        [],
    // Extended schema (v2)
    lastMessage:     "",
    tierUsed:        tier,
    entitySnapshot:  entityContext
      ? { type: entityContext.type, id: entityContext.id, name: entityContext.name }
      : null,
    // Future-ready flags
    pinned:          false,
    archived:        false,
    unread:          false,
  };
}
